<template>
  <q-table
    v-if="debugProperties.length !== 0"
    hide-bottom
    bordered
    flat
    dense
    row-key="id"
    :rows="rows"
    :columns="columns"
    :rows-per-page-options="[0]"
    :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
  >
    <template #header-cell="props">
      <q-th :props="props">
        {{ props.col.label }}
        <q-tooltip v-if="props.col.description" max-width="250px">
          <b>{{ props.col.label }}</b>
          <br />
          {{ props.col.description }}
        </q-tooltip>
      </q-th>
    </template>

    <template #body-cell-street_address_alias="{ value }">
      <q-td>
        <div v-for="alias in value" :key="alias">{{ alias }}</div>
      </q-td>
    </template>
    <template #body-cell-building_name_alias="{ value }">
      <q-td>
        <div v-for="alias in value" :key="alias">{{ alias }}</div>
      </q-td>
    </template>

    <template #body-cell-listing_urls="{ value }">
      <q-td>
        <div v-for="key in Object.keys(value).filter((k) => value[k].length)" :key="key">
          <a
            class="text-primary cursor-pointer"
            style="text-decoration: none"
            target="_blank"
            :href="value[key].length === 1 && !nonUrlSource(key) ? value[key][0] : undefined"
          >
            {{ key }} ({{ value[key].length }})
          </a>
          <q-menu
            v-if="value[key].length > 1 || nonUrlSource(key)"
            transition-duration="100"
            class="q-pa-sm"
          >
            <q-markdown-table flat>
              <q-tr v-for="url in value[key]" :key="url">
                <q-td>
                  <a
                    :href="!nonUrlSource(key) ? url : undefined"
                    target="_blank"
                    :class="{ 'text-primary': !nonUrlSource(key) }"
                  >
                    {{ url }}
                  </a>
                </q-td>
              </q-tr>
            </q-markdown-table>
          </q-menu>
        </div>
      </q-td>
    </template>

    <template #body-cell-sources="{ row }">
      <q-td align="center">
        <q-btn color="primary" icon="link" flat dense size="sm" @click="openSources(row)">
          <q-tooltip>Sources</q-tooltip>
        </q-btn>
      </q-td>
    </template>

    <template #body-cell-helpful_links="{ row }">
      <q-td align="center">
        <q-btn
          :style="!row.building_website ? 'visibility: hidden' : ''"
          color="primary"
          icon="link"
          flat
          dense
          size="sm"
          :href="row.building_website"
          target="_blank"
        >
          <q-tooltip>Property Website</q-tooltip>
        </q-btn>
        <q-btn
          color="primary"
          icon="map"
          flat
          dense
          size="sm"
          :href="`https://www.google.com/maps/dir/${row.street_address_alias.map((alias: string) => [alias, row.city, row.state, row.zip_code].join(' ')).join('/')}`"
          target="_blank"
        >
          <q-tooltip>Google Maps</q-tooltip>
        </q-btn>
      </q-td>
    </template>

    <!-- ✏️ Edit Property column -->
    <template #body-cell-edit_property="{ row }">
      <q-td align="center">
        <q-btn color="primary" icon="edit" flat dense size="sm" @click="openEdit(row)">
          <q-tooltip>Edit Property</q-tooltip>
        </q-btn>
      </q-td>
    </template>
  </q-table>

  <q-dialog v-model="showSourcesDialog" persistent>
    <q-card class="full-width" style="max-width: 90vw">
      <q-card-section class="row items-center justify-between sticky-header">
        <div class="text-h6">Sources</div>
        <q-btn icon="close" flat round dense @click="showSourcesDialog = false" />
      </q-card-section>
      <q-card-section>
        <div v-if="selectedProperty">
          <!-- Building level -->
          <div class="text-subtitle2 q-mb-xs">Building Sources</div>
          <q-markup-table flat bordered dense>
            <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <tr>
                <th id="attribute" style="width: 20%" scope="col">Attribute</th>
                <th id="value" style="width: 35%" scope="col">Value</th>
                <th id="sources" style="width: 45%" scope="col">Sources</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in selectedProperty.building_source" :key="row.attribute">
                <td style="width: 20%">{{ row.attribute }}</td>
                <td
                  style="
                    width: 35%;
                    word-wrap: break-word;
                    word-break: break-word;
                    white-space: pre-wrap;
                  "
                >
                  <div v-if="valueList(row.value).length">
                    <div v-for="v in valueList(row.value)" :key="v">{{ v }}</div>
                  </div>
                  <template v-else>{{ row.value }}</template>
                </td>
                <td style="width: 45%">
                  <template v-if="Array.isArray(row.sources_url)">
                    <div v-for="url in row.sources_url" :key="url">
                      <a :href="url" target="_blank" class="text-primary">{{ url }}</a>
                    </div>
                  </template>
                  <template v-else>
                    <div v-for="(list, key) in row.sources_url" :key="key">
                      <div class="text-bold q-mt-xs">{{ key }}</div>
                      <div v-for="url in list" :key="url">
                        <a :href="url" target="_blank" class="text-primary">{{ url }}</a>
                      </div>
                    </div>
                  </template>
                </td>
              </tr>
            </tbody>
          </q-markup-table>

          <q-separator class="q-my-md" />

          <!-- Unit level -->
          <div class="text-subtitle2 q-mb-xs">Unit Sources</div>
          <q-markup-table flat bordered dense>
            <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <tr>
                <th id="unit" scope="col">Unit</th>
                <th id="avail" scope="col">Avail.</th>
                <th id="enter" scope="col">Enter</th>
                <th id="exit" scope="col">Exit</th>
                <th id="created" scope="col">Created</th>
                <th id="url" scope="col">URL</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in selectedProperty.unit_sources" :key="u.unit_name + u.created_on">
                <td>{{ u.unit_name }}</td>
                <td>{{ u.availability }}</td>
                <td>{{ u.enter_market }}</td>
                <td>{{ u.exit_market }}</td>
                <td>{{ u.created_on }}</td>
                <td>
                  <a :href="u.url" target="_blank" class="text-primary">{{ u.url }}</a>
                </td>
              </tr>
            </tbody>
          </q-markup-table>

          <template v-if="selectedProperty.concessions_history?.length">
            <q-separator class="q-my-md" />

            <!-- Concession History level -->
            <div class="text-subtitle2 q-mb-xs">Concession History Sources</div>
            <q-markup-table flat bordered dense>
              <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                <tr>
                  <th id="specials" scope="col" style="width: 35%">Specials</th>
                  <th id="from_date" scope="col">From Date</th>
                  <th id="to_date" scope="col">To Date</th>
                  <th id="type" scope="col">Type</th>
                  <th id="source" scope="col" style="width: 30%">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(concession, index) in selectedProperty.concessions_history"
                  :key="index"
                >
                  <td
                    style="
                      width: 35%;
                      word-wrap: break-word;
                      word-break: break-word;
                      white-space: pre-wrap;
                    "
                  >
                    {{ concession.specials }}
                  </td>
                  <td>{{ concession.from_date }}</td>
                  <td>{{ concession.to_date }}</td>
                  <td>{{ concession.type }}</td>
                  <td style="width: 30%">
                    <a
                      v-if="concession.source"
                      :href="concession.source"
                      target="_blank"
                      class="text-primary"
                      style="word-wrap: break-word; word-break: break-all"
                    >
                      {{ concession.source }}
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
          </template>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>

  <!-- 🚧 Edit Property dialog -->
  <q-dialog v-model="showEditDialog" persistent>
    <q-card class="full-width" style="max-width: 90vw">
      <q-card-section class="row items-center justify-between">
        <div class="text-h6">Edit Property</div>
        <q-btn icon="close" flat round dense @click="showEditDialog = false" />
      </q-card-section>
      <q-card-section class="q-gutter-md">
        <!-- Rename Address Section -->
        <div class="q-mb-lg">
          <div class="text-h6 q-mb-md">Rename Address</div>
          <div class="row q-gutter-md">
            <!-- Where Section -->
            <div class="col">
              <div class="text-subtitle2 q-mb-sm">Where</div>
              <q-input
                v-model="renameForm.where.streetAddress"
                label="Street Address"
                outlined
                dense
                class="q-mb-sm"
              />
              <q-input
                v-model="renameForm.where.buildingName"
                label="Building Name"
                outlined
                dense
              />
            </div>

            <!-- Set Street Address To Section -->
            <div class="col">
              <div class="text-subtitle2 q-mb-sm">Set Street Address To</div>
              <q-input
                v-model="renameForm.setTo.streetAddress"
                label="Street Address"
                outlined
                dense
              />
            </div>
          </div>
          <div class="q-mt-md">
            <q-btn color="primary" @click="submitRenameAddress" :disable="!canSubmitRename">
              Submit Rename
            </q-btn>
          </div>
        </div>

        <q-separator />

        <!-- Force Split Section -->
        <div class="q-mt-lg">
          <div class="text-h6 q-mb-md">Force Split</div>
          <div class="row q-gutter-md">
            <!-- Address Group One -->
            <div class="col">
              <div class="text-subtitle2 q-mb-sm">Address Group One</div>
              <div
                v-for="(address, index) in forceSplitForm.groupOne"
                :key="`group1-${index}`"
                class="row q-gutter-sm q-mb-sm"
              >
                <q-input
                  v-model="forceSplitForm.groupOne[index]"
                  label="Street Address"
                  outlined
                  dense
                  class="col"
                />
                <q-btn
                  color="negative"
                  icon="remove"
                  flat
                  dense
                  @click="removeAddressFromGroup('groupOne', index)"
                  :disable="forceSplitForm.groupOne.length === 1"
                />
              </div>
              <q-btn
                color="positive"
                icon="add"
                label="Add Address"
                flat
                dense
                @click="addAddressToGroup('groupOne')"
              />
            </div>

            <!-- Address Group Two -->
            <div class="col">
              <div class="text-subtitle2 q-mb-sm">Address Group Two</div>
              <div
                v-for="(address, index) in forceSplitForm.groupTwo"
                :key="`group2-${index}`"
                class="row q-gutter-sm q-mb-sm"
              >
                <q-input
                  v-model="forceSplitForm.groupTwo[index]"
                  label="Street Address"
                  outlined
                  dense
                  class="col"
                />
                <q-btn
                  color="negative"
                  icon="remove"
                  flat
                  dense
                  @click="removeAddressFromGroup('groupTwo', index)"
                  :disable="forceSplitForm.groupTwo.length === 1"
                />
              </div>
              <q-btn
                color="positive"
                icon="add"
                label="Add Address"
                flat
                dense
                @click="addAddressToGroup('groupTwo')"
              />
            </div>
          </div>
          <div class="q-mt-md">
            <q-btn color="primary" @click="submitForceSplit" :disable="!canSubmitForceSplit">
              Submit Force Split
            </q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type {
  PropertyDetailsResponse,
  Availability,
  Price,
  AvailabilityPeriod,
  DebugProperty
} from 'common'
import { useAdminStore } from '@/stores/admin'

export default {
  name: 'PropertyCompareDebug',
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array as PropType<
        {
          prop: PropertyDetailsResponse
          units: { unit: Availability; lastHistory: Price; period: AvailabilityPeriod }[]
        }[]
      >,
      required: true
    }
  },
  data() {
    return {
      debugProperties: [] as DebugProperty[],
      showSourcesDialog: false,
      showEditDialog: false,
      selectedProperty: null as DebugProperty | null,
      // Form data
      renameForm: {
        where: {
          streetAddress: '',
          buildingName: ''
        },
        setTo: {
          streetAddress: ''
        }
      },
      forceSplitForm: {
        groupOne: [''],
        groupTwo: ['']
      }
    }
  },
  computed: {
    columns() {
      return [
        {
          field: 'id',
          name: 'id',
          label: 'Property ID',
          align: 'left',
          sortable: true,
          description: 'The id of this property, helpful primarily for the dev team.'
        },
        {
          field: 'street_address_alias',
          name: 'street_address_alias',
          label: 'Street Addresses',
          align: 'left',
          description:
            'All of the street address aliases for this building. Seeing wrong aliases is a good sign that we must force split this property.'
        },
        {
          field: 'building_name_alias',
          name: 'building_name_alias',
          label: 'Building Names',
          align: 'left',
          description: 'All building name aliases.'
        },
        {
          field: (row: PropertyDetailsResponse) => {
            const lookup: Record<string, string[]> = {
              'American Campus': [],
              'Apartment List': [],
              Apartments: [],
              Appfolio: [],
              Entrata: [],
              GooglePlace: [],
              Knock: [],
              OnSite: [],
              RealPage: [],
              RentCafe: [],
              SightMap: [],
              StreetEasy: [],
              Yardi: [],
              Zillow: [],
              Zumper: [],
              Mri: [],
              Unknown: []
            }
            const debugProperty = this.debugProperties.find((_) => _.id === row.id)
            if (debugProperty) {
              for (const url of debugProperty.listing_urls) {
                if (url.includes('americancampus.com')) {
                  lookup['American Campus'].push(url)
                } else if (url.includes('apartmentlist.com')) {
                  lookup['Apartment List'].push(url)
                } else if (url.includes('.mriprospectconnect')) {
                  lookup['Mri'].push(url)
                } else if (url.includes('apartments.com')) {
                  lookup['Apartments'].push(url)
                } else if (url.includes('appfolio.com')) {
                  lookup['Appfolio'].push(url)
                } else if (url.includes('knock-')) {
                  lookup['Knock'].push(url.slice(6))
                } else if (url.startsWith('onsite-')) {
                  lookup['OnSite'].push(
                    `https://www.on-site.com/web/online_app/choose_unit?property_id=${url.slice(7)}`
                  )
                } else if (url.includes('rentcafe.com')) {
                  lookup['RentCafe'].push(url)
                } else if (url.includes('streeteasy.com')) {
                  lookup['StreetEasy'].push(url)
                } else if (url.startsWith('yardi-')) {
                  lookup['Yardi'].push(
                    `https://www.rentcafe.com/onlineleasing/apartmentsforrent/oleapplication.aspx?stepname=floorplan&myOlePropertyId=${url.slice(6)}`
                  )
                } else if (url.includes('zillow.com')) {
                  lookup['Zillow'].push(url)
                } else if (url.includes('zumper.com')) {
                  lookup['Zumper'].push(url)
                } else if (/^[0-9]+$/.test(url)) {
                  lookup['RealPage'].push(url)
                } else if (/^[a-zA-Z0-9]+$/.test(url) && url.length === 11) {
                  lookup['SightMap'].push(`https://sightmap.com/embed/${url}`)
                } else if (
                  url.endsWith('/student/') ||
                  url.endsWith('/conventional/') ||
                  url.endsWith('/affordable/') ||
                  url.endsWith('/military/')
                ) {
                  lookup['Entrata'].push(url)
                } else if (url.startsWith('Ch') && url.length === 27) {
                  lookup['GooglePlace'].push(
                    `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${url}`
                  )
                } else {
                  lookup['Unknown'].push(url)
                }
              }
            }
            return lookup
          },
          name: 'listing_urls',
          label: 'Listing Urls',
          align: 'left',
          sortable: true,
          description: 'All of the listing urls that were fed into this record.'
        },
        {
          field: (row: PropertyDetailsResponse) => {
            const dp = this.debugProperties.find((d) => d.id === row.id)
            return dp?.latest_unit_source || ''
          },
          name: 'latest_unit_source',
          label: 'Unit Source',
          align: 'center',
          sortable: true,
          description: 'The source that is powering the latest unit availabilities.'
        },
        { field: '', name: 'sources', label: 'Sources', align: 'center', description: 'Sources' },
        {
          field: '',
          name: 'edit_property',
          label: 'Edit Property',
          align: 'center',
          description: 'Edit property metadata'
        },
        {
          field: '',
          name: 'helpful_links',
          label: 'Helpful Links',
          align: 'center',
          description: 'Helpful Links'
        }
      ]
    },
    rows() {
      return this.filteredProperties.map((f) => f.prop)
    },
    canSubmitRename() {
      return (
        this.renameForm.where.streetAddress.trim() !== '' &&
        this.renameForm.setTo.streetAddress.trim() !== ''
      )
    },
    canSubmitForceSplit() {
      const validGroupOne = this.forceSplitForm.groupOne.some((addr) => addr.trim() !== '')
      const validGroupTwo = this.forceSplitForm.groupTwo.some((addr) => addr.trim() !== '')
      return validGroupOne && validGroupTwo
    }
  },
  created() {
    useAdminStore()
      .debugProperties(this.filteredProperties.map((f) => f.prop.id))
      .then((props) => {
        this.debugProperties = props
      })
      .catch((error) => {
        console.error('Failed to fetch debug properties:', error)
        // Consider showing a user notification or retry mechanism
        this.$q.notify({
          type: 'negative',
          message: 'Failed to load debug information'
        })
      })
  },
  methods: {
    nonUrlSource(src: string) {
      return ['RealPage', 'Knock'].includes(src)
    },
    openSources(row: PropertyDetailsResponse) {
      this.selectedProperty = this.debugProperties.find((d) => d.id === row.id) || null
      this.showSourcesDialog = true
    },
    openEdit(row: PropertyDetailsResponse) {
      this.selectedProperty = this.debugProperties.find((d) => d.id === row.id) || null
      this.resetForms()
      this.showEditDialog = true
    },
    resetForms() {
      this.renameForm = {
        where: {
          streetAddress: '',
          buildingName: ''
        },
        setTo: {
          streetAddress: ''
        }
      }
      this.forceSplitForm = {
        groupOne: [''],
        groupTwo: ['']
      }
    },
    addAddressToGroup(group: 'groupOne' | 'groupTwo') {
      this.forceSplitForm[group].push('')
    },
    removeAddressFromGroup(group: 'groupOne' | 'groupTwo', index: number) {
      if (this.forceSplitForm[group].length > 1) {
        this.forceSplitForm[group].splice(index, 1)
      }
    },
    submitRenameAddress() {
      console.log('Rename Address Form Data:', this.renameForm)
      // Add your API call logic here
      // Example:
      // await useAdminStore().renameAddress(this.selectedProperty?.id, this.renameForm)
    },
    submitForceSplit() {
      const formData = {
        groupOne: this.forceSplitForm.groupOne.filter((addr) => addr.trim() !== ''),
        groupTwo: this.forceSplitForm.groupTwo.filter((addr) => addr.trim() !== '')
      }
      console.log('Force Split Form Data:', formData)
      // Add your API call logic here
      // Example:
      // await useAdminStore().forceSplit(this.selectedProperty?.id, formData)
    },
    valueList(val: any): string[] {
      if (Array.isArray(val)) return val
      if (val instanceof Set) return Array.from(val)
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val)
          if (Array.isArray(parsed)) return parsed
        } catch {}
        const splitters = [',', ';', '|']
        for (const s of splitters) {
          if (val.includes(s))
            return val
              .split(s)
              .map((v) => v.trim())
              .filter(Boolean)
        }
      }
      return []
    }
  }
}
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
}

.q-dark .sticky-header {
  background-color: var(--q-dark-page);
}
</style>
